/** Note naming, pitch maths and the keyboard's absolute-index coordinate system. */

export const LETTERS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;
export const SOLFEGE = ['Do', 'Do#', 'Re', 'Re#', 'Mi', 'Fa', 'Fa#', 'Sol', 'Sol#', 'La', 'La#', 'Si'] as const;

/** Semitone offsets within an octave that are white keys on a piano. */
export const WHITE_SET: ReadonlySet<number> = new Set([0, 2, 4, 5, 7, 9, 11]);

export type Notation = 'letters' | 'solfege' | 'none';

/**
 * Notes are addressed by "absolute index" = octave * 12 + semitone, i.e. C0 is
 * 0 and C8 is 96. This is MIDI shifted by one octave; it exists because the UI
 * thinks in scientific octave numbers (C4 = middle C).
 */
export const FULL_RANGE_START = 9; // A0 — bottom of a concert piano
export const FULL_RANGE_END = 96; // C8 — top of a concert piano (88 keys inclusive)
export const MIN_ABS = FULL_RANGE_START;
export const MAX_ABS = FULL_RANGE_END;

export interface NoteAddress {
  readonly octave: number;
  readonly noteIndex: number;
}

export const absIndex = (octave: number, noteIndex: number): number => octave * 12 + noteIndex;

export const fromAbsIndex = (idx: number): NoteAddress => ({
  octave: Math.floor(idx / 12),
  noteIndex: ((idx % 12) + 12) % 12,
});

export const isWhite = (noteIndex: number): boolean => WHITE_SET.has(noteIndex);


/** Tone.js note name, e.g. "C#4". */
export const toneName = (idx: number): string => {
  const { octave, noteIndex } = fromAbsIndex(idx);
  return `${LETTERS[noteIndex]}${octave}`;
};

/** Display label in the chosen notation, without the octave number. */
export const noteLabel = (noteIndex: number, notation: Notation): string => {
  if (notation === 'none') return '';
  return (notation === 'solfege' ? SOLFEGE : LETTERS)[noteIndex] ?? '';
};

export const frequencyOf = (idx: number): number => {
  const midi = idx + 12; // absolute index 0 == C0 == MIDI 12
  return 440 * Math.pow(2, (midi - 69) / 12);
};

const NOTE_BASE: Readonly<Record<string, number>> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** Parse a Tone note name ("A#1", "C4") to an absolute index, or null. */
export const parseNoteName = (name: string): number | null => {
  const m = /^([A-G])(#?)(-?\d+)$/.exec(name);
  if (!m) return null;
  const base = NOTE_BASE[m[1] as string];
  if (base === undefined) return null;
  return Number(m[3]) * 12 + base + (m[2] ? 1 : 0);
};

export const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
